import { useState } from "react";
import ApiService from "../services/api";

const ReportsPage = ({ invoices, quotes, clients, settings }) => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Start of year
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  const [reportType, setReportType] = useState('revenue');
  const [loading, setLoading] = useState(false);
  const [includeInactiveClients, setIncludeInactiveClients] = useState(false);

  const formatCurrency = (amount) => `${settings?.currency || '$'}${parseFloat(amount || 0).toFixed(2)}`;
  const formatDate = (date) => new Date(date).toLocaleDateString();

  const calculateTotal = (item) => {
    const subtotal = item.items?.reduce((sum, i) => sum + (i.quantity * i.price), 0) || 0;
    const discount = subtotal * (item.discountPercentage || 0) / 100;
    const afterDiscount = subtotal - discount;
    const tax = settings?.taxExempt ? 0 : afterDiscount * (settings?.taxPercentage || 10) / 100;
    return afterDiscount + tax;
  };

  // Filter data by date range
  const filteredInvoices = invoices.filter(inv => {
    const invDate = new Date(inv.date);
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    return invDate >= start && invDate <= end;
  });

  const filteredQuotes = quotes.filter(quote => {
    const quoteDate = new Date(quote.date);
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    return quoteDate >= start && quoteDate <= end;
  });

  // Revenue calculations
  const totalRevenue = filteredInvoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + calculateTotal(inv), 0);

  const outstandingRevenue = filteredInvoices
    .filter(inv => inv.status === 'sent' || inv.status === 'overdue')
    .reduce((sum, inv) => sum + calculateTotal(inv), 0);

  const draftRevenue = filteredInvoices
    .filter(inv => inv.status === 'draft')
    .reduce((sum, inv) => sum + calculateTotal(inv), 0);

  const quotesValue = filteredQuotes
    .filter(quote => quote.status === 'pending')
    .reduce((sum, quote) => sum + calculateTotal(quote), 0);

  // Client analysis - show all clients with comprehensive data
  const clientsData = clients
    .filter(client => includeInactiveClients || client.isActive)
    .map(client => {
      // All invoices for this client (not just paid ones)
      const clientInvoices = filteredInvoices.filter(inv => inv.clientId === client._id);
      const paidInvoices = clientInvoices.filter(inv => inv.status === 'paid');
      const pendingInvoices = clientInvoices.filter(inv => inv.status === 'sent' || inv.status === 'overdue');
      const draftInvoices = clientInvoices.filter(inv => inv.status === 'draft');

      // All quotes for this client
      const clientQuotes = filteredQuotes.filter(quote => quote.clientId === client._id);

      // Revenue calculations
      const paidRevenue = paidInvoices.reduce((sum, inv) => sum + calculateTotal(inv), 0);
      const pendingRevenue = pendingInvoices.reduce((sum, inv) => sum + calculateTotal(inv), 0);
      const draftRevenue = draftInvoices.reduce((sum, inv) => sum + calculateTotal(inv), 0);
      const quotesValue = clientQuotes.reduce((sum, quote) => sum + calculateTotal(quote), 0);

      // Find last invoice and quote dates
      const lastInvoice = clientInvoices.length > 0
        ? Math.max(...clientInvoices.map(inv => new Date(inv.date)))
        : null;
      const lastQuote = clientQuotes.length > 0
        ? Math.max(...clientQuotes.map(quote => new Date(quote.date)))
        : null;

      return {
        ...client,
        totalInvoices: clientInvoices.length,
        paidInvoices: paidInvoices.length,
        pendingInvoices: pendingInvoices.length,
        draftInvoices: draftInvoices.length,
        totalQuotes: clientQuotes.length,
        paidRevenue,
        pendingRevenue,
        draftRevenue,
        quotesValue,
        totalValue: paidRevenue + pendingRevenue + draftRevenue + quotesValue,
        lastInvoiceDate: lastInvoice ? new Date(lastInvoice) : null,
        lastQuoteDate: lastQuote ? new Date(lastQuote) : null,
        averageInvoice: paidInvoices.length > 0 ? paidRevenue / paidInvoices.length : 0
      };
    })
    .sort((a, b) => b.totalValue - a.totalValue);

  // Monthly breakdown
  const monthlyData = {};
  filteredInvoices.forEach(inv => {
    if (inv.status === 'paid') {
      const month = new Date(inv.date).toISOString().substring(0, 7); // YYYY-MM
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, count: 0 };
      }
      monthlyData[month].revenue += calculateTotal(inv);
      monthlyData[month].count += 1;
    }
  });

  const monthlyReport = Object.entries(monthlyData)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, data]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
      ...data
    }));

  // Overdue analysis
  const overdueInvoices = filteredInvoices.filter(inv => inv.status === 'overdue');
  const overdueByClient = {};
  overdueInvoices.forEach(inv => {
    const client = clients.find(c => c._id === inv.clientId);
    const clientName = client?.name || 'Unknown Client';
    if (!overdueByClient[clientName]) {
      overdueByClient[clientName] = { count: 0, amount: 0 };
    }
    overdueByClient[clientName].count += 1;
    overdueByClient[clientName].amount += calculateTotal(inv);
  });

  // Quote conversion analysis
  const quoteStats = {
    total: filteredQuotes.length,
    pending: filteredQuotes.filter(q => q.status === 'pending').length,
    accepted: filteredQuotes.filter(q => q.status === 'accepted').length,
    rejected: filteredQuotes.filter(q => q.status === 'rejected').length,
    converted: filteredQuotes.filter(q => q.status === 'converted').length
  };

  const conversionRate = quoteStats.total > 0
    ? ((quoteStats.accepted + quoteStats.converted) / quoteStats.total * 100).toFixed(1)
    : 0;

  const exportReport = async (exportFormat) => {
    setLoading(true);
    try {
      // Prepare report data based on current report type
      let reportData = {};

      switch (reportType) {
        case 'revenue':
          reportData = {
            totalRevenue,
            outstandingRevenue,
            draftRevenue,
            quotesValue,
            monthlyReport
          };
          break;
        case 'clients':
          reportData = {
            clientsData
          };
          break;
        case 'overdue':
          reportData = {
            overdueInvoices: overdueInvoices.map(inv => ({
              ...inv,
              clientName: clients.find(c => c._id === inv.clientId)?.name || 'Unknown Client',
              total: calculateTotal(inv)
            })),
            overdueByClient
          };
          break;
        case 'quotes':
          reportData = {
            quotes: filteredQuotes.map(quote => ({
              ...quote,
              clientName: clients.find(c => c._id === quote.clientId)?.name || 'Unknown Client',
              total: calculateTotal(quote)
            })),
            quoteStats,
            conversionRate
          };
          break;
        default:
          reportData = {};
          break;
      }

      const response = await ApiService.exportReport(
        reportData,
        reportType,
        exportFormat.toLowerCase(),
        dateRange
      );

      if (!response.success) {
        alert('Failed to generate export file');
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderRevenueReport = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h6 className="text-lg font-semibold text-gray-800">Revenue Summary</h6>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="text-2xl font-bold text-green-600 mb-1">{formatCurrency(totalRevenue)}</h4>
              <small className="text-gray-500">Total Revenue</small>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="text-2xl font-bold text-yellow-600 mb-1">{formatCurrency(outstandingRevenue)}</h4>
              <small className="text-gray-500">Outstanding</small>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="text-2xl font-bold text-gray-600 mb-1">{formatCurrency(draftRevenue)}</h4>
              <small className="text-gray-500">Draft Value</small>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="text-2xl font-bold text-cyan-600 mb-1">{formatCurrency(quotesValue)}</h4>
              <small className="text-gray-500">Pending Quotes</small>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h6 className="text-lg font-semibold text-gray-800">Monthly Revenue Trend</h6>
        </div>
        <div className="p-6">
          {monthlyReport.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 font-medium text-gray-700">Month</th>
                    <th className="text-right py-2 font-medium text-gray-700">Revenue</th>
                    <th className="text-right py-2 font-medium text-gray-700">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyReport.slice(-6).map((month, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2">{month.month}</td>
                      <td className="text-right py-2 font-medium">{formatCurrency(month.revenue)}</td>
                      <td className="text-right py-2">{month.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center">No revenue data for selected period</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderClientReport = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h6 className="text-lg font-semibold text-gray-800">Clients Report</h6>
        <div className="flex items-center">
          <input
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            type="checkbox"
            id="includeInactiveClients"
            checked={includeInactiveClients}
            onChange={(e) => setIncludeInactiveClients(e.target.checked)}
          />
          <label className="ml-2 text-sm text-gray-700" htmlFor="includeInactiveClients">
            Include inactive clients
          </label>
        </div>
      </div>
      <div className="p-6">
        {clientsData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 font-medium text-gray-700">Client</th>
                  <th className="text-right py-3 font-medium text-gray-700">Status</th>
                  <th className="text-right py-3 font-medium text-gray-700">Paid</th>
                  <th className="text-right py-3 font-medium text-gray-700">Pending</th>
                  <th className="text-right py-3 font-medium text-gray-700">Draft</th>
                  <th className="text-right py-3 font-medium text-gray-700">Quotes</th>
                  <th className="text-right py-3 font-medium text-gray-700">Total Value</th>
                  <th className="text-right py-3 font-medium text-gray-700">Invoices</th>
                  <th className="text-right py-3 font-medium text-gray-700">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {clientsData.map((client, index) => (
                  <tr key={client._id} className="hover:bg-gray-50 border-b border-gray-100">
                    <td className="py-3">
                      <div className="flex items-center">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-600 text-white text-xs font-medium rounded mr-3">
                          {index + 1}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900">{client.name}</div>
                          <div className="text-xs text-gray-500">{client.email}</div>
                          {client.phone && <div className="text-xs text-gray-500">{client.phone}</div>}
                          {client.category && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                              {client.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-right py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${client.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {client.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-right py-3 text-green-600 font-medium">
                      {formatCurrency(client.paidRevenue)}
                    </td>
                    <td className="text-right py-3 text-yellow-600 font-medium">
                      {formatCurrency(client.pendingRevenue)}
                    </td>
                    <td className="text-right py-3 text-gray-600 font-medium">
                      {formatCurrency(client.draftRevenue)}
                    </td>
                    <td className="text-right py-3 text-cyan-600 font-medium">
                      {formatCurrency(client.quotesValue)}
                    </td>
                    <td className="text-right py-3 font-bold text-gray-900">
                      {formatCurrency(client.totalValue)}
                    </td>
                    <td className="text-right py-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {client.totalInvoices}
                      </span>
                    </td>
                    <td className="text-right py-3 text-gray-600">
                      {client.lastInvoiceDate || client.lastQuoteDate
                        ? formatDate(
                          client.lastInvoiceDate && client.lastQuoteDate
                            ? Math.max(client.lastInvoiceDate, client.lastQuoteDate)
                            : client.lastInvoiceDate || client.lastQuoteDate
                        )
                        : 'N/A'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center">No clients found for selected criteria</p>
        )}
      </div>
    </div>
  );

  const renderOverdueReport = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h6 className="text-lg font-semibold text-gray-800">Overdue Invoices</h6>
        </div>
        <div className="p-6">
          {overdueInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 font-medium text-gray-700">Invoice</th>
                    <th className="text-left py-3 font-medium text-gray-700">Client</th>
                    <th className="text-left py-3 font-medium text-gray-700">Due Date</th>
                    <th className="text-left py-3 font-medium text-gray-700">Days Overdue</th>
                    <th className="text-right py-3 font-medium text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueInvoices.map(invoice => {
                    const client = clients.find(c => c._id === invoice.clientId);
                    const daysOverdue = Math.floor((new Date() - new Date(invoice.dueDate)) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={invoice._id} className="hover:bg-gray-50 border-b border-gray-100">
                        <td className="py-3 font-medium text-gray-900">{invoice.number}</td>
                        <td className="py-3 text-gray-700">{client?.name || 'Unknown Client'}</td>
                        <td className="py-3 text-gray-700">{formatDate(invoice.dueDate)}</td>
                        <td className="py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${daysOverdue > 30 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {daysOverdue} days
                          </span>
                        </td>
                        <td className="text-right py-3 font-medium text-red-600">
                          {formatCurrency(calculateTotal(invoice))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h5 className="text-lg font-semibold text-green-600 mb-2">No Overdue Invoices!</h5>
              <p className="text-gray-500">All invoices are current</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h6 className="text-lg font-semibold text-gray-800">Overdue by Client</h6>
        </div>
        <div className="p-6">
          {Object.keys(overdueByClient).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(overdueByClient)
                .sort(([, a], [, b]) => b.amount - a.amount)
                .map(([clientName, data]) => (
                  <div key={clientName} className="flex justify-between items-center p-3 border border-gray-200 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">{clientName}</div>
                      <div className="text-sm text-gray-500">{data.count} invoice(s)</div>
                    </div>
                    <span className="font-bold text-red-600">
                      {formatCurrency(data.amount)}
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center">No overdue invoices</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderQuoteReport = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h6 className="text-lg font-semibold text-gray-800">Quote Statistics</h6>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 text-center mb-6">
            <div>
              <h3 className="text-3xl font-bold text-blue-600">{quoteStats.total}</h3>
              <small className="text-gray-500">Total Quotes</small>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-green-600">{conversionRate}%</h3>
              <small className="text-gray-500">Conversion Rate</small>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-yellow-600 mb-1">{quoteStats.pending}</div>
              <small className="text-gray-500">Pending</small>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-green-600 mb-1">{quoteStats.accepted}</div>
              <small className="text-gray-500">Accepted</small>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-cyan-600 mb-1">{quoteStats.converted}</div>
              <small className="text-gray-500">Converted</small>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-red-600 mb-1">{quoteStats.rejected}</div>
              <small className="text-gray-500">Rejected</small>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h6 className="text-lg font-semibold text-gray-800">Recent Quotes</h6>
        </div>
        <div className="p-6">
          {filteredQuotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 font-medium text-gray-700">Number</th>
                    <th className="text-left py-2 font-medium text-gray-700">Client</th>
                    <th className="text-left py-2 font-medium text-gray-700">Status</th>
                    <th className="text-right py-2 font-medium text-gray-700">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.slice(-10).reverse().map(quote => {
                    const client = clients.find(c => c._id === quote.clientId);
                    return (
                      <tr key={quote._id} className="border-b border-gray-100">
                        <td className="py-2 font-medium text-gray-900">{quote.number}</td>
                        <td className="py-2 text-gray-700">{client?.name || 'Unknown'}</td>
                        <td className="py-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              quote.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                quote.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-cyan-100 text-cyan-800'
                            }`}>
                            {quote.status}
                          </span>
                        </td>
                        <td className="text-right py-2 text-gray-900">
                          {formatCurrency(calculateTotal(quote))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center">No quotes for selected period</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header with controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-3">
              <h5 className="text-xl font-semibold text-gray-800">Reports & Analytics</h5>
            </div>
            <div className="md:col-span-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="md:col-span-3">
              <div className="flex gap-2">
                <button
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors disabled:opacity-50"
                  onClick={() => exportReport('PDF')}
                  disabled={loading}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  PDF
                </button>
                <button
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                  onClick={() => exportReport('Excel')}
                  disabled={loading}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Excel
                </button>
                <button
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                  onClick={() => exportReport('CSV')}
                  disabled={loading}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report type tabs */}
      <nav className="flex space-x-1 mb-6">
        <button
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${reportType === 'revenue'
              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          onClick={() => setReportType('revenue')}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          Revenue
        </button>
        <button
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${reportType === 'clients'
              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          onClick={() => setReportType('clients')}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          Clients
        </button>
        <button
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${reportType === 'overdue'
              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          onClick={() => setReportType('overdue')}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.308 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          Overdue
        </button>
        <button
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${reportType === 'quotes'
              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          onClick={() => setReportType('quotes')}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Quotes
        </button>
      </nav>

      {/* Report content */}
      <div>
        {reportType === 'revenue' && renderRevenueReport()}
        {reportType === 'clients' && renderClientReport()}
        {reportType === 'overdue' && renderOverdueReport()}
        {reportType === 'quotes' && renderQuoteReport()}
      </div>
    </div>
  );
};

export default ReportsPage;